import React from 'react';
import PropTypes from 'prop-types';
import { Spacing, Text, Tooltip } from '@reservamos/elements';
import { useTranslation } from 'react-i18next';
import LocalizedCurrency from 'components/LocalizedCurrency';
import { isDesktopSize } from 'utils/Reserbus';

const DEFAULT_WIDTH_BOX = 200;
const CUSTOM_ROWS_WIDTH_BOX = 280;

/**
 * Extras component.
 *
 * @param {Object} props - The component props.
 * @param {number} props.total - The total prop.
 * @param {string} props.text - The text prop.
 * @returns {JSX.Element} The rendered component.
 */
const Extras = ({ total, text }) => {
  return (
    <Spacing justifyContent="space-between">
      <Text size="XS" color="white">
        {text}
      </Text>

      <LocalizedCurrency
        price={total}
        size="XS"
        weight="bold"
        color="white"
        currencyStyle="currency"
      />
    </Spacing>
  );
};

Extras.propTypes = {
  total: PropTypes.number,
  text: PropTypes.string,
};

/**
 * PricingTooltipDetails component.
 *
 * @param {Object} props - The component props.
 * @param {number} props.luggage - The luggage prop.
 * @param {number} props.networks - The networks prop.
 * @param {number} props.taxes - The taxes prop.
 * @param {number} props.insurance - The insurance prop.
 * @param {Array<{label: string, amount: number}>} [props.rows] - Custom breakdown rows; when given they replace the default rows.
 * @param {string} [props.legend] - Legend rendered under the custom rows.
 *
 * @returns {JSX.Element} The rendered component.
 */
const PricingTooltipDetails = ({ luggage, networks, taxes, insurance, rows, legend }) => {
  const { t } = useTranslation('');
  const isMobile = !isDesktopSize();
  const hasCustomRows = Boolean(rows && rows.length);
  // Custom rows carry long labels plus a legend: at the default 200px every row
  // wraps to two or three lines and the box grows tall enough to overflow above
  // the viewport. A wider box keeps the breakdown short and fully visible.
  const widthBox = hasCustomRows ? CUSTOM_ROWS_WIDTH_BOX : DEFAULT_WIDTH_BOX;
  return (
    <Tooltip
      type="top"
      horizontal={isMobile ? 'left' : 'right'}
      widthBox={widthBox}
      customContent={
        <Spacing vertical size="XS">
          {hasCustomRows ? (
            <>
              {rows.map((row) => (
                <Extras key={row.label} total={row.amount} text={row.label} />
              ))}
              {legend && (
                <Text size="XS" color="white">
                  {legend}
                </Text>
              )}
            </>
          ) : (
            <>
              {luggage >= 0 && <Extras total={luggage} text={t('purchase:luggage')} />}
              {networks >= 0 && <Extras total={networks} text={t('purchase:networks')} />}
              {taxes >= 0 && <Extras total={taxes} text={t('purchase:label.taxes')} />}
              {insurance >= 0 && <Extras total={insurance} text={t('purchase:insurance')} />}
            </>
          )}
        </Spacing>
      }
    >
      <Text color="primary" underline>
        {t('general:show_details_new')}
      </Text>
    </Tooltip>
  );
};

PricingTooltipDetails.propTypes = {
  luggage: PropTypes.number,
  networks: PropTypes.number,
  taxes: PropTypes.number,
  insurance: PropTypes.number,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      amount: PropTypes.number.isRequired,
    }),
  ),
  legend: PropTypes.string,
};

PricingTooltipDetails.defaultProps = {
  rows: null,
  legend: null,
};

export default PricingTooltipDetails;
